import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { NrBadge } from "@/components/DriverRequests";

export function NrBulkDialog({ open, onClose, drivers, customerFilter }) {
  const { user: me } = useAuth();
  const [picked, setPicked] = useState({});
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState("nationalregistry.fmcsa.dot.gov lookup");
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  const eligible = useMemo(() => drivers
    .filter((d) => d.medical_card_expiry && !["terminated", "self_terminated"].includes(d.status) && (!customerFilter || customerFilter === "all" || d.customer_id === customerFilter))
    .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)), [drivers, customerFilter]);
  const shown = eligible.filter((d) => !q || `${d.first_name} ${d.last_name}`.toLowerCase().includes(q.toLowerCase()));
  const needs = (d) => { const nr = d.nr_verification || {}; return !nr.verified_on || (nr.med_card_expiry && nr.med_card_expiry !== d.medical_card_expiry); };

  useEffect(() => { if (open) { setPicked(Object.fromEntries(eligible.map((d) => [d.id, needs(d)]))); setQ(""); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const ids = Object.keys(picked).filter((k) => picked[k]);

  const save = async () => {
    if (!ids.length) { toast.error("Select at least one driver"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/drivers/nr-verification/bulk", { driver_ids: ids, verified_on: date, method });
      toast.success(`National Registry verified for ${data.updated} driver${data.updated === 1 ? "" : "s"} — signed as ${me?.name}`, { description: data.skipped_no_med_card.length ? `Skipped (no med card on file): ${data.skipped_no_med_card.join(", ")}` : undefined });
      onClose(true);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="nr-bulk-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-600" />Bulk National Registry verification</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">After checking each examiner on <b>nationalregistry.fmcsa.dot.gov</b>, tick the drivers you verified. Each record is dated and e-signed as <b>{me?.name}</b>, and the Verification Record in their DQ binder pre-fills. Drivers needing a (re)verification are pre-selected.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Date of verification</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="nr-bulk-date" /></div>
          <div><Label>Method</Label><Input value={method} onChange={(e) => setMethod(e.target.value)} data-testid="nr-bulk-method" /></div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find driver…" className="h-8 max-w-xs" data-testid="nr-bulk-search" />
          <div className="text-xs text-slate-600 flex items-center gap-3"><span data-testid="nr-bulk-count">{ids.length} of {eligible.length} selected</span><button type="button" className="underline" onClick={() => setPicked(Object.fromEntries(shown.map((d) => [d.id, true])))}>All</button><button type="button" className="underline" onClick={() => setPicked({})}>None</button></div>
        </div>
        <div className="border border-slate-200 rounded-md max-h-[40vh] overflow-y-auto divide-y divide-slate-100" data-testid="nr-bulk-list">
          {shown.map((d) => (
            <label key={d.id} className="flex items-center gap-3 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50">
              <Checkbox checked={!!picked[d.id]} onCheckedChange={(v) => setPicked({ ...picked, [d.id]: !!v })} data-testid={`nr-bulk-pick-${d.id}`} />
              <span className="flex-1"><b>{d.first_name} {d.last_name}</b> <span className="text-slate-400 text-xs">· med card exp {d.medical_card_expiry}</span></span>
              <NrBadge driver={d} />
            </label>
          ))}
          {shown.length === 0 && <div className="p-4 text-sm text-slate-500 text-center">No drivers with a medical card match.</div>}
        </div>
        <DialogFooter><Button onClick={save} disabled={saving || !ids.length} className="bg-emerald-600 hover:bg-emerald-700" data-testid="nr-bulk-save">{saving ? "Saving…" : `Verify & sign for ${ids.length} driver${ids.length === 1 ? "" : "s"}`}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
